/**
 * Single React Query client for the whole app.
 *
 * Exported as a module singleton so plain service functions (which run outside
 * React) can de-duplicate their fetches through the same cache the components
 * use — one request per key per page load instead of one per caller.
 */
import { QueryClient } from '@tanstack/react-query';

/** Catalogue-style reference data: rarely changes within a session. */
export const CATALOGUE_STALE_MS = 10 * 60 * 1000;
/** Per-user roles / entitlements: short-lived but still de-duplicated. */
export const IDENTITY_STALE_MS = 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Fetch through the shared cache. Concurrent callers with the same key share
 * one in-flight request; later callers inside `staleTime` get the cached value.
 */
export const cachedFetch = <T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  staleTime = CATALOGUE_STALE_MS,
): Promise<T> => queryClient.fetchQuery({ queryKey, queryFn, staleTime });
